import { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
// Import Components
import NavigationItemCircle from './NavigationItemCircle';
import AccessControl from 'components/common/AccessControl';
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
// Import Hooks
import { useGTMEvent } from 'hooks/UseGTM';
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
import useHasPermission from 'hooks/UseHasPermission';
// Import Types
import { NavigationGroup, RenderMenuGroupOptions } from './types';
// Import Constants
import { BLOCKED_BY_SUBSCRIPTION_CLASSNAME, l, GACategoryEvents } from 'constants/common';
// Import Enums
import { SubscriptionSectionEnum } from 'enums';

const { GAPredefinedCategory } = GACategoryEvents;

export const RenderMenuGroup = (group: NavigationGroup, options: RenderMenuGroupOptions) => {
    const { mainGroup, predefinedGroup } = group;

    // Hooks
    const { getRoutePath, goToRoute } = useGoToRoute();

    const filterHolderQueryParams = useQueryParams('filterHolderPredefinedList'); // TODO create const for this

    const { logEvent } = useGTMEvent();
    const { t } = useTranslation();
    const { hasSubscriptionSection } = useHasPermission();

    const {
        keyPrefix,
        gaPrefix,
        gaSuffix,
        gaCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
        clickOverride,
    } = options;

    const mainGroupMapContent = useMemo(() => {
        return (
            <div className="crm-h-c-cnt-content">
                {mainGroup.map((item) => {
                    const {
                        iconClass,
                        displayNameKey,
                        routeModel,
                        permission,
                        partnerSetting,
                        mandatoryPermissions,
                        subscriptionSection,
                        hasChildren,
                        contentViewKey,
                    } = item;

                    const clickHandler = (e) => {
                        if (e.ctrlKey || e.metaKey || e.button === 1) return;
                        e.preventDefault();
                        e.stopPropagation();

                        if (clickOverride) {
                            // for custom cases like award modal
                            clickOverride(displayNameKey);
                            return;
                        }

                        // analytics
                        logEvent(
                            gaCategory.name,
                            gaCategory.events[`${gaPrefix}${displayNameKey}${gaSuffix}`],
                            displayNameKey,
                        );

                        if (hasChildren && contentViewKey) {
                            setCreatePopoverView(contentViewKey);
                        } else {
                            setCreateVisibilityStatus(false);
                            if (routeModel) {
                                goToRoute(routeModel);
                            }
                        }
                    };

                    const isBlockedBySubscription =
                        subscriptionSection && hasSubscriptionSection
                            ? !hasSubscriptionSection(subscriptionSection)
                            : false;

                    const content = (
                        <NavigationItemCircle
                            iconClass={iconClass}
                            displayNameKey={t(displayNameKey)}
                            clickHandler={clickHandler}
                            route={routeModel}
                        />
                    );

                    return (
                        <AccessControl
                            key={`${keyPrefix}-${displayNameKey}`}
                            permissions={permission}
                            partnerSetting={partnerSetting}
                            mandatoryPermissions={mandatoryPermissions}
                        >
                            {isBlockedBySubscription ? (
                                <BlockedBySubscriptionInfo isBlocked={isBlockedBySubscription}>
                                    <div
                                        className={classNames({
                                            [BLOCKED_BY_SUBSCRIPTION_CLASSNAME]: isBlockedBySubscription,
                                            'not-clickable': isBlockedBySubscription,
                                        })}
                                    >
                                        {content}
                                    </div>
                                </BlockedBySubscriptionInfo>
                            ) : (
                                content
                            )}
                        </AccessControl>
                    );
                })}
            </div>
        );
    }, [
        clickOverride,
        gaCategory.events,
        gaCategory.name,
        gaPrefix,
        gaSuffix,
        goToRoute,
        hasSubscriptionSection,
        keyPrefix,
        logEvent,
        mainGroup,
        setCreatePopoverView,
        setCreateVisibilityStatus,
        t,
    ]);

    const predefinedGroupContent = useMemo(() => {
        if (!predefinedGroup) return null;

        const queryString = filterHolderQueryParams.encode({
            ObjectType: String(predefinedGroup.objectType),
        });

        const hrefWithQuery = `${getRoutePath(predefinedGroup.routeModel)}${queryString}`;

        const isPredefinedBlockedBySubscription = !hasSubscriptionSection(
            predefinedGroup?.subscriptionSection as SubscriptionSectionEnum,
        );

        const handlePredefinedClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.stopPropagation();
            e.preventDefault();

            if (predefinedGroup.gaEvent) {
                logEvent(GAPredefinedCategory.name, predefinedGroup.gaEvent, predefinedGroup.displayNameKey);
            }

            setCreateVisibilityStatus(false);
            goToRoute(predefinedGroup.routeModel, undefined, queryString);
        };

        return (
            <div className="crm-h-c-cnt-footer">
                <div className="devider-with-text">{t(l.Or)}</div>
                <div className="crm-h-c-cnt-f-inner">
                    <BlockedBySubscriptionInfo isBlocked={isPredefinedBlockedBySubscription}>
                        <div
                            className={classNames('predefined-anchor-wrapper', {
                                [BLOCKED_BY_SUBSCRIPTION_CLASSNAME]: isPredefinedBlockedBySubscription,
                                'not-clickable': isPredefinedBlockedBySubscription,
                            })}
                        >
                            <a href={hrefWithQuery} onClick={handlePredefinedClick}>
                                {t(predefinedGroup.displayNameKey)}
                            </a>
                            <div className="ai-badge-new">{t(l.New)}</div>
                        </div>
                    </BlockedBySubscriptionInfo>
                </div>
            </div>
        );
    }, [
        filterHolderQueryParams,
        getRoutePath,
        goToRoute,
        hasSubscriptionSection,
        logEvent,
        predefinedGroup,
        setCreateVisibilityStatus,
        t,
    ]);

    return { mainGroupMapContent, predefinedGroupContent };
};
